'use client';

import { useEffect, useState } from 'react';

const UPDATE_CHECK_INTERVAL = 60 * 1000; // 60 seconds

export function useServiceWorker() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    // Only run in browser with service worker support
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    let refreshing = false;

    // Reload page when new service worker takes control
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };

    // Register service worker
    const registerServiceWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');

        // Check if there's a waiting service worker
        const checkForWaiting = () => {
          if (registration.waiting) {
            setWaitingWorker(registration.waiting);
            setUpdateAvailable(true);
          }
        };

        // Listen for new service worker installing
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker is installed but waiting
              setWaitingWorker(newWorker);
              setUpdateAvailable(true);
            }
          });
        });

        // Check immediately in case there's already a waiting worker
        checkForWaiting();

        // Check for updates periodically (every 60 seconds)
        const interval = setInterval(() => {
          registration.update().catch(() => {
            // Silently fail update checks
          });
        }, UPDATE_CHECK_INTERVAL);

        // Listen for controller change
        navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

        return () => {
          clearInterval(interval);
          navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        };
      } catch (error) {
        console.error('Service worker registration failed:', error);
      }
    };

    registerServiceWorker();
  }, []);

  const updateApp = () => {
    if (!waitingWorker) return;

    // Send SKIP_WAITING message to the waiting service worker
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  };

  return { updateAvailable, updateApp };
}
